import { useTrans } from "@/translations";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ColumnDef,
  ColumnSort,
  Row,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { isEqual, noop } from "lodash";
import { twMerge } from "tailwind-merge";

import { TableContext } from "./TableContext";
import { TableBody } from "./components/TableBody";
import { TableFooter } from "./components/TableFooter";
import { TableHeader } from "./components/TableHeader";
import { TableSkeleton } from "./components/TableSkeleton";
import { TableCellRenderFunctionsType } from "./utils/cellRenderFunctions";
import { getColumns } from "./utils/getColumns";

export type DataTableDataType = string | number;
export type Header = {
  key: string;
  title: string;
  extraPopupText?: string;
  translate?: boolean;
  defaultSize?: number;
  maxSize?: number;
  rightAlign?: boolean;
  sortable?: boolean;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  renderFunction?: TableCellRenderFunctionsType[string];
  isClickable?: boolean;
};

export type ExpandedColumnDef = ColumnDef<DataTableDataType> & {
  rightAlign?: boolean;
  accessorKey: string;
  extraPopupText?: string;
  isClickable?: boolean;
};

export type Pagination = {
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isRefetching?: boolean;
};

export type ExpandOptions = {
  subRowKey: string;
  enabled?: boolean;
};

type TableProps = {
  data?: { [k: string]: any }[];
  defaultSortingHeader?: ColumnSort;
  selectedIds?: string[];
  setSelectedIds?: (ids: string[]) => void;
  isLoading?: boolean;
  onRowClick?: (rowData: any) => void;
  onRowSelectionChange?: (rowSelection: Record<number, boolean>) => void;
  headers?: Header[];
  useCheckboxes?: boolean;
  hasMultipleSelectFunctionality?: boolean;
  hasExportText?: boolean;
  showExportButton?: boolean;
  labelSingle?: string;
  labelMultiple?: string;
  showFooter?: boolean;
  getStickyFooterComponent?: (ref: HTMLTableElement) => React.JSX.Element;
  stickyCheckbox?: boolean;
  updateSorting?: (sorting: SortingState) => void;
  pagination?: Pagination;
  onAllSelected?: (val: boolean) => void;
  useActions?: boolean;
  onEdit?: (row: any) => void;
  onDelete?: (id: string) => void;
  getEditDisabled?: (row: any) => boolean;
  getDeleteDisabled?: (row: any) => boolean;
  expandOptions?: ExpandOptions;
};

const getPinningLeft = (
  headers: Header[],
  stickyCheckbox: boolean,
  expandEnabled?: boolean,
) => {
  const left = [];
  if (expandEnabled) left.push("expand");
  if (stickyCheckbox) left.push("checkbox");
  return left.concat(
    headers.filter((header) => header.stickyLeft).map((header) => header.key),
  );
};

export const TableWithSelect: React.FC<TableProps> = ({
  data = [],
  defaultSortingHeader,
  selectedIds = [],
  setSelectedIds = () => {},
  isLoading,
  onRowClick = () => {},
  onRowSelectionChange = () => {},
  headers = [],
  useCheckboxes = true,
  hasMultipleSelectFunctionality = true,
  hasExportText = true,
  showExportButton = true,
  labelSingle = "",
  labelMultiple = "",
  showFooter: shouldShowFooter = true,
  getStickyFooterComponent = () => <></>,
  stickyCheckbox = false,
  updateSorting = noop,
  pagination,
  onAllSelected = () => {},
  useActions,
  onEdit,
  onDelete,
  getEditDisabled,
  getDeleteDisabled,
  expandOptions: { subRowKey, enabled: expandEnabled } = { enabled: false },
}) => {
  const { t } = useTrans();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const skeletonWrapperRef = useRef<HTMLDivElement | null>(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [isSelectedRowLoading, setIsSelectedLoading] = useState(
    selectedIds.length === 1,
  );
  const [sorting, setSorting] = useState<SortingState>(
    defaultSortingHeader ? [defaultSortingHeader] : [],
  );

  useEffect(() => {
    updateSorting(sorting);
  }, [sorting, updateSorting]);

  const columns = useMemo<ColumnDef<any>[]>(
    () =>
      getColumns({
        headers,
        data,
        t,
        selectedIds,
        setSelectedIds,
        useCheckboxes,
        onAllSelected,
        useActions,
        onEdit,
        onDelete,
        getEditDisabled,
        getDeleteDisabled,
        useExpand: expandEnabled,
      }),
    [
      data,
      headers,
      t,
      selectedIds,
      setSelectedIds,
      useCheckboxes,
      onAllSelected,
      useActions,
      onEdit,
      onDelete,
      getEditDisabled,
      getDeleteDisabled,
      expandEnabled,
    ],
  );

  const setRowSelection = useCallback(
    (updaterFn: any) => {
      const updatedSelectedRows = updaterFn(selectedRows);
      if (!isEqual(selectedRows, updatedSelectedRows)) {
        setSelectedRows(updaterFn);
        onRowSelectionChange(updatedSelectedRows);
      }
    },
    [selectedRows, onRowSelectionChange],
  );

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: "onEnd",
    state: {
      rowSelection: selectedRows,
      sorting,
      columnPinning: {
        left: getPinningLeft(headers, stickyCheckbox, expandEnabled),
        right: headers
          .filter((header) => header.stickyRight)
          .map((header) => header.key),
      },
    },
    getRowId: (row) => row.id,
    getSubRows: expandEnabled ? (row) => row[subRowKey] : undefined,
    getExpandedRowModel: expandEnabled ? getExpandedRowModel() : undefined,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: pagination ? undefined : getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
  });

  const rows = table.getRowModel().rows;

  // Select rows based on the selectedIds context
  useEffect(() => {
    rows.forEach((row) => {
      const shouldToggle = selectedIds.includes(row.original.id as string);
      row.toggleSelected(shouldToggle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, rows]);

  const virtualizerInstance = useVirtualizer({
    count: rows.length,
    estimateSize: () => 50, // row height
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
  });

  const onRowClickFunction = useCallback(
    (row: Row<any>) => {
      row.toggleSelected();
      row.toggleExpanded(true);
      onRowClick(row.original);
    },
    [onRowClick],
  );

  const contextValue = useMemo(
    () => ({
      onRowClick: onRowClickFunction,
      selectedIds,
    }),
    [onRowClickFunction, selectedIds],
  );

  // Called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (!pagination) return;
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        // Once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !pagination.isRefetching &&
          !pagination.isFetchingNextPage &&
          pagination.hasNextPage
        ) {
          pagination.fetchNextPage();
        }
      }
    },
    [pagination],
  );

  useEffect(() => {
    if (selectedIds.length === 1) {
      const index = rows.findIndex((el) => el.original.id === selectedIds[0]);

      const selectedRow = rows.find((el) => el.getIsSelected());

      if (index >= 0 && selectedRow?.original.id !== selectedIds[0]) {
        virtualizerInstance.scrollToIndex(index);
        setIsSelectedLoading(false);
      } else if (index < 0 && pagination?.hasNextPage === false) {
        setIsSelectedLoading(false);
      }
    }
  }, [selectedIds, rows, virtualizerInstance, pagination?.hasNextPage]);

  const stickyFooter = getStickyFooterComponent(
    tableRef.current as HTMLTableElement,
  );

  const showFooter = useMemo(() => {
    if (!!pagination) {
      return (
        shouldShowFooter &&
        (table.getSelectedRowModel().rows.length > 0 || hasExportText)
      );
    }
    return shouldShowFooter;
  }, [shouldShowFooter, table, hasExportText, pagination]);

  return (
    <TableContext.Provider value={contextValue}>
      <div
        className={twMerge(
          `grid h-full grid-rows-[1fr_64px] p-5`,
          !showFooter && "grid-rows-[1fr]",
        )}
      >
        <div
          className={twMerge(
            "flex h-full w-full flex-col overflow-auto",
            (isLoading || isSelectedRowLoading) && "relative overflow-hidden",
          )}
          ref={tableContainerRef}
          onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        >
          {!isLoading && !!rows.length && (
            <>
              <table
                className="relative min-w-full table-fixed"
                {...{
                  ref: tableRef,
                }}
              >
                <TableHeader headers={table.getHeaderGroups()} />
                {!isLoading && (
                  <TableBody
                    rows={rows}
                    virtualizerInstance={virtualizerInstance}
                  />
                )}
                {!isLoading && <>{stickyFooter}</>}
              </table>
            </>
          )}
          {(isLoading || isSelectedRowLoading) && (
            <div
              className="absolute h-full w-full bg-white leading-tight"
              ref={skeletonWrapperRef}
            >
              <TableSkeleton wrapperRef={skeletonWrapperRef} />
            </div>
          )}
        </div>
        {showFooter && (
          <TableFooter
            numberTotalRows={table.getPreFilteredRowModel().rows.length}
            numberRowsSelected={table.getSelectedRowModel().rows.length}
            {...{
              labelMultiple,
              labelSingle,
              hasExportText,
              hasMultipleSelectFunctionality,
              showExportButton,
              isPaginated: !!pagination,
              hasLoadedEverything: !pagination?.hasNextPage,
            }}
          />
        )}
      </div>
    </TableContext.Provider>
  );
};
